<div class="mb-md flex items-center justify-between gap-md font-semibold text-support">
  <span class="text-base">{{ title }}</span>
  <div class="flex items-center gap-sm">
    <label class="whitespace-nowrap" for="selected_period">{{ 'Selected year:' | translate }}</label>
    <select
      sbInput
      id="selected_period"
      class="max-w-fit"
      [ngModel]="year"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="yearChange.emit($event)"
    >
      @for (yearOption of years; track yearOption) {
        <option [value]="yearOption">{{ yearOption }}</option>
      }
    </select>
  </div>
</div>

<stat-summary>
  <stat-summary-block>
    <stat-summary-value [loading]="!stats?.totals">
      {{ stats?.totals?.plus_min_start | localeDuration: 'digital' }}
    </stat-summary-value>
    <span statSummaryTitle>
      {{ 'Start balance' | translate }} {{ year }}
      <sb-icon
        name="information-regular"
        [sbTooltipPlacement]="'top'"
        [sbTooltip]="'Number of plus minus hours at the start of the selected year.' | translate"
      />
    </span>
  </stat-summary-block>
  <stat-summary-block>
    <stat-summary-value [loading]="!stats?.totals">
      {{ stats?.totals?.plus_min | localeDuration: 'digital' }}
    </stat-summary-value>
    <span statSummaryTitle>
      {{ 'Plus minus period' | translate }}
      <sb-icon name="information-regular" [sbTooltip]="'Plus minus hours in selected year.' | translate" />
    </span>
  </stat-summary-block>
  <stat-summary-block>
    <stat-summary-value [loading]="!stats?.totals">
      {{ stats?.totals?.plus_min_total_to_last_week | localeDuration: 'digital' }}
    </stat-summary-value>
    <span statSummaryTitle>
      {{ 'Plus minus last week' | translate }}
      <sb-icon
        name="information-regular"
        [sbTooltip]="'Plus minus hours at the start of the current week.' | translate"
      />
    </span>
  </stat-summary-block>

  <stat-summary-footer>
    <stat-summary-footer-item>
      <sb-icon name="file-regular" size="xl" />
      {{ 'Contract' | translate }}:
      @if (stats?.totals) {
        <span>{{ stats?.totals?.contract_hours | localeDuration: 'digital' }}</span>
      } @else {
        <sb-spinner size="sm" />
      }
    </stat-summary-footer-item>
    <stat-summary-footer-item>
      <sb-icon name="stopwatch-regular" size="xl" />
      {{ 'Worked' | translate }}:
      @if (stats?.totals) {
        <span>{{ stats?.totals?.worked | localeDuration: 'digital' }}</span>
      } @else {
        <sb-spinner size="sm" />
      }
    </stat-summary-footer-item>
    @if (showAbsence) {
      @for (absence of absenteeOptions | slice: 0 : 2; track absence) {
        <stat-summary-footer-item>
          <sb-icon [name]="absence.icon" size="xl" />
          {{ absence.option }}:
          @if (stats?.totals) {
            <span>{{ stats.totals['option_' + absence.id] | localeDuration: 'digital' }}</span>
          } @else {
            <sb-spinner size="sm" />
          }
        </stat-summary-footer-item>
      }
    } @else {
      <stat-summary-footer-item>
        <sb-icon name="calendar-slash" size="xl" />
        {{ 'Absence' | translate }}:
        @if (stats?.totals) {
          <span>{{ stats?.totals?.absent | localeDuration: 'digital' }}</span>
        } @else {
          <sb-spinner size="sm" />
        }
      </stat-summary-footer-item>
    }
    <stat-summary-footer-item>
      <sb-icon name="calendar-slash" size="xl" />
      {{ 'Correction' | translate }}:
      @if (stats?.totals) {
        <span>{{ stats?.totals?.correction_plus_min | localeDuration: 'digital' }}</span>
      } @else {
        <sb-spinner size="sm" />
      }
    </stat-summary-footer-item>
  </stat-summary-footer>
</stat-summary>

<div class="mt-lg mb-md flex items-center gap-xs">
  <h2 class="reset-h text-lg font-semibold text-default">{{ 'Overview' | translate }}</h2>
  <knowledge-base-article [article]="'plus-minus-hours'"></knowledge-base-article>
</div>

@if (stats?.week_stats | toArray; as weekStats) {
  @if (loadingStats) {
    <sb-loading-state class="py-2xl" />
  } @else if (!weekStats.length) {
    <sb-card>
      <sb-empty-state class="py-2xl" icon="inbox" [description]="'No data' | translate" />
    </sb-card>
  } @else {
    <div sbTableContainer [innerScroll]="true" class="block max-h-70v">
      <table sbTable [stickyHeader]="true">
        <thead>
          <tr sbTableRow>
            <th sbTableHeaderCell sbStickyColumn colspan="2">{{ 'Week nr.' | translate }}</th>
            <th sbTableHeaderCell class="max-sm:hidden">{{ 'Period' | translate }}</th>
            <th sbTableHeaderCell class="max-sm:hidden">{{ 'Contract hours' | translate }}</th>
            <th sbTableHeaderCell>{{ 'Worked (regular)' | translate }}</th>
            <th sbTableHeaderCell>{{ 'Worked (overtime)' | translate }}</th>
            <th sbTableHeaderCell>{{ 'Absent' | translate }}</th>
            <th sbTableHeaderCell class="max-sm:hidden">{{ 'Time surcharge' | translate }}</th>
            <th sbTableHeaderCell class="max-sm:hidden">{{ 'Correction' | translate }}</th>
            <th sbTableHeaderCell>{{ 'Period' | translate }}</th>
            <th sbTableHeaderCell sbStickyColumn="right">{{ 'Total' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (weekStat of weekStats?.slice().reverse(); track weekStat) {
            <tr sbTableRow>
              <td sbTableCell sbStickyColumn>
                <button
                  sb-icon-button
                  color="transparent"
                  [attr.aria-label]="'Show week detail' | translate"
                  [attr.aria-expanded]="!!showWeeks[weekStat.weeknumber]"
                  (click)="showWeeks[weekStat.weeknumber] = !showWeeks[weekStat.weeknumber]"
                >
                  @if (!showWeeks[weekStat.weeknumber]) {
                    <sb-icon name="down-regular" size="sm" />
                  }
                  @if (showWeeks[weekStat.weeknumber] && stats.day_stats) {
                    <sb-icon name="up-regular" size="sm" />
                  }
                  @if (showWeeks[weekStat.weeknumber] && !stats.day_stats) {
                    <sb-spinner size="sm" />
                  }
                </button>
              </td>
              <td sbTableCell sbStickyColumn>{{ weekStat.weeknumber }}</td>
              <td sbTableCell class="whitespace-nowrap max-sm:hidden">
                {{ [weekStat.start, weekStat.end] | localeDate: 'short' }}
              </td>
              <td sbTableCell class="max-sm:hidden">{{ weekStat.contract_hours | localeDuration: 'digital' : '-' }}</td>
              <td sbTableCell>{{ weekStat.regular_time | localeDuration: 'digital' : '-' }}</td>
              <td sbTableCell>{{ weekStat.overtime | localeDuration: 'digital' : '-' }}</td>
              <td sbTableCell>{{ weekStat.absent | localeDuration: 'digital' : '-' }}</td>
              <td sbTableCell class="max-sm:hidden">{{ weekStat.surcharge_time | localeDuration: 'digital' : '-' }}</td>
              <td sbTableCell class="max-sm:hidden">
                {{ weekStat.correction_plus_min | localeDuration: 'digital' : '-' }}
              </td>
              <td sbTableCell><balance [value]="weekStat.plus_min"></balance></td>
              <td sbTableCell sbStickyColumn="right"><balance [value]="weekStat.plus_min_end"></balance></td>
            </tr>
            @if (showWeeks[weekStat.weeknumber]) {
              @for (
                dayStat of stats.day_stats ? stats.day_stats[weekStat.year + weekStat.weeknumber]?.slice() : [];
                track dayStat
              ) {
                <tr sbTableRow>
                  <td sbTableCell sbStickyColumn colspan="2">{{ dayStat.date | localeDate: 'weekday' }}</td>
                  <td sbTableCell class="max-sm:hidden">{{ dayStat.date | localeDate }}</td>
                  <td sbTableCell class="max-sm:hidden">
                    {{ dayStat.contract_hours | localeDuration: 'digital' : '-' }}
                  </td>
                  <td sbTableCell>{{ dayStat.regular_time | localeDuration: 'digital' : '-' }}</td>
                  <td sbTableCell>{{ dayStat.overtime | localeDuration: 'digital' : '-' }}</td>
                  @if (dayStat.absent && showAbsence) {
                    <td sbTableCell>
                      @for (absenteeOption of absenteeOptions; track absenteeOption) {
                        @if (dayStat['option_' + absenteeOption.id]) {
                          <div>
                            {{ dayStat['option_' + absenteeOption.id] | localeDuration: 'digital' }} ({{
                              absenteeOption.option
                            }})
                          </div>
                        }
                      }
                    </td>
                  }
                  @if (dayStat.absent && !showAbsence) {
                    <td sbTableCell>{{ dayStat.absent | localeDuration: 'digital' }}</td>
                  }
                  @if (!dayStat.absent) {
                    <td sbTableCell>-</td>
                  }
                  <td sbTableCell class="max-sm:hidden">
                    {{ dayStat.surcharge_time | localeDuration: 'digital' : '-' }}
                  </td>
                  <td sbTableCell class="max-sm:hidden">
                    {{ dayStat.correction_plus_min | localeDuration: 'digital' : '-' }}
                  </td>
                  <td sbTableCell><balance [value]="dayStat.plus_min"></balance></td>
                  <td sbTableCell sbStickyColumn="right"><balance [value]="dayStat.plus_min_end"></balance></td>
                </tr>
              }
            }
          }
        </tbody>
      </table>
    </div>
  }
}

<div class="mt-lg mb-md flex items-center justify-between gap-md">
  <div class="flex items-center gap-xs">
    <h2 class="reset-h text-lg font-semibold text-default">{{ 'Plus minus corrections' | translate }}</h2>
    <knowledge-base-article [article]="'plus/minus-hour-correction'"></knowledge-base-article>
  </div>
  @if (canEditPlusMinHours()) {
    <a
      sb-button
      color="primary"
      [routerLink]="['', { outlets: { modal: ['time-off-correction', 'overtime', userId] } }]"
    >
      <sb-icon name="plus" />
      {{ 'Add correction' | translate }}
    </a>
  }
</div>

@if (loadingCorrections) {
  <sb-loading-state class="py-2xl" />
} @else if (!stats?.corrections.length) {
  <sb-card>
    <sb-empty-state class="py-2xl" icon="inbox" [description]="'No corrections' | translate" />
  </sb-card>
} @else {
  <div sbTableContainer [innerScroll]="true">
    <table sbTable>
      <thead>
        <tr sbTableRow>
          <th sbTableHeaderCell>{{ 'Date' | translate }}</th>
          <th sbTableHeaderCell>{{ 'Hours' | translate }}</th>
          <th sbTableHeaderCell>{{ 'Pay' | translate }}</th>
          <th sbTableHeaderCell>{{ 'Payout date' | translate }}</th>
          <th sbTableHeaderCell>{{ 'Note' | translate }}</th>
          <th sbTableHeaderCell>{{ 'Created by' | translate }}</th>
          <th sbTableHeaderCell>{{ 'Updated' | translate }}</th>
          @if (canEditPlusMinHours()) {
            <th sbTableHeaderCell></th>
          }
        </tr>
      </thead>
      <tbody>
        @for (correction of stats?.corrections; track correction) {
          <tr sbTableRow>
            <td sbTableCell>{{ correction.date | localeDate }}</td>
            <td sbTableCell><balance [value]="correction.hours"></balance></td>
            @if (correction.pay) {
              <td sbTableCell>{{ 'Yes' | translate }}</td>
            } @else {
              <td sbTableCell>{{ 'No' | translate }}</td>
            }
            <td sbTableCell>{{ correction.payout_date | localeDate }}</td>
            <td sbTableCell class="w-[350px] break-all whitespace-pre-line">{{ correction.note }}</td>
            <td sbTableCell data-hj-suppress>{{ correction.createdByName }}</td>
            <td sbTableCell>{{ correction.updated | localeDate: 'dateTimeShort' }}</td>
            @if (canEditPlusMinHours()) {
              <td sbTableCell>
                <button
                  sbActionConfirm
                  sb-icon-button
                  color="transparent"
                  (confirmed)="removeCorrection.emit(correction.id)"
                >
                  <sb-icon name="delete-2-regular" />
                  <span class="sr-only">{{ 'Delete correction' | translate }}</span>
                </button>
              </td>
            }
          </tr>
        }
      </tbody>
    </table>
  </div>
}
